import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Message, SenderType } from '@openmirror/shared';
import { useActualApi } from '../hooks/useActualApi';
import { useAuth } from './AuthContext';
import { useClient } from './ClientContext';
import { API_BASE_URL } from '../constants';

export interface TestChatMessage extends Message {
  triggerActivated?: boolean;
  triggerName?: string;
  confidence?: number;
}

interface TestChatContextType {
  messages: TestChatMessage[];
  isTesting: boolean;
  isLoading: boolean;
  // true only while resetting the test session (separate from isLoading which is used for send/start processing)
  isResetting: boolean;
  error: string | null;
  sendTestMessage: (text: string) => Promise<void>;
  resetSession: () => Promise<void>;
  startSession: () => Promise<void>;
  sessionExists: boolean;
  messageCount: number;
}

const TestChatContext = createContext<TestChatContextType | undefined>(undefined);

export const TestChatProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { idToken } = useAuth();
  const { selectedClientId } = useClient();
  const api = useActualApi();

  const [messages, setMessages] = useState<TestChatMessage[]>([]);
  const [isTesting, setIsTesting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Separate flag used only while resetting the test session so UI controls (like the Reset button)
  // can be toggled independently of message processing/loading.
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionExists, setSessionExists] = useState(false);
  const [messageCount, setMessageCount] = useState(0);

  // Check session status on mount
  useEffect(() => {
    if (idToken) {
      checkSessionStatus();
    }
  }, [idToken]);

  // When the AI Persona page is opened, load messages from the dashboard API
  // instead of subscribing directly to Firestore. This uses the backend endpoints
  // already implemented in the dashboard API.
  useEffect(() => {
    if (!idToken) return;
    if (typeof window === 'undefined' || !window.location) return;
    // Only load when on the ai-persona route
    if (window.location.pathname !== '/ai-persona') return;

    // Load session info and messages via dashboard API
    (async () => {
      try {
        await checkSessionStatus();
        await loadMessages();
      } catch (err) {
        console.error('Failed to load test chat via dashboard API:', err);
      }
    })();
  }, [idToken]);

  const checkSessionStatus = async () => {
    try {
      // Using a custom API for session status (use absolute API base so dev/prod both work)
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      };
      if (selectedClientId) headers['X-Client-ID'] = selectedClientId;
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/client/test_chat/session_info`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        // Non-2xx response -> no active session or server error
        setSessionExists(false);
        setMessageCount(0);
        return;
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        setSessionExists(Boolean(data.session_exists));
        setMessageCount(data.message_count || 0);
      } else {
        // Received HTML (likely frontend dev server) or other non-JSON response — log snippet for debugging
        const text = await response.text();
        console.error('checkSessionStatus: expected JSON but got non-JSON response:', text.slice(0, 400));
        setSessionExists(false);
        setMessageCount(0);
      }
    } catch (err) {
      console.error('Failed to check session status:', err);
      setSessionExists(false);
      setMessageCount(0);
    }
  };

  const loadMessages = async () => {
    try {
      // Using a custom fetch for messages
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      };
      if (selectedClientId) headers['X-Client-ID'] = selectedClientId;
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/client/test_chat/messages`, {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Failed to load test messages:', err);
    }
  };

  const startSession = useCallback(async () => {
    if (!idToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const headers: Record<string, string> = {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      };
      if (selectedClientId) headers['X-Client-ID'] = selectedClientId;
      const resp = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/client/test_chat/start_session`, {
        method: 'POST',
        headers,
      });

      if (!resp.ok) {
        const errBody = await resp.json().catch(() => ({ message: 'Failed to start test session' }));
        throw new Error(errBody.message || 'Failed to start test session');
      }

      // Update local state and load any existing messages
      setSessionExists(true);
      await checkSessionStatus();
      await loadMessages();
    } catch (err: any) {
      setError(err.message || 'Failed to start session');
      console.error('startSession error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [idToken]);

  const sendTestMessage = useCallback(async (text: string) => {
    if (!idToken || isLoading) return;

    setIsLoading(true);
    setError(null);
    setIsTesting(true);

    try {
      // Create user message
      const userMessage: TestChatMessage = {
        id: `msg_test_user_${Date.now()}`,
        sender: SenderType.USER,
        text: text.trim(),
        timestamp: new Date().toISOString(),
      };

      // Optimistically add user message
      setMessages(prev => [...prev, userMessage]);

      // Process message through test chat API
      const sendHeaders: Record<string, string> = {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      };
      if (selectedClientId) sendHeaders['X-Client-ID'] = selectedClientId;
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/client/test_chat/process_message`, {
        method: 'POST',
        headers: sendHeaders,
        body: JSON.stringify({
          message: text.trim(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process test message');
      }

      const data = await response.json();

      // Create bot response message
      const botMessage: TestChatMessage = {
        id: data.bot_message_id,
        sender: SenderType.BOT,
        text: data.bot_response,
        timestamp: new Date().toISOString(),
        triggerActivated: data.trigger_activated,
        triggerName: data.trigger_name,
        confidence: data.confidence,
      };

      // Add bot message to state
      setMessages(prev => [...prev, botMessage]);

      // Update session status
      await checkSessionStatus();

    } catch (err: any) {
      setError(err.message || 'Failed to send test message');
      console.error('Test message error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [idToken, isLoading]);


  const resetSession = useCallback(async () => {
    if (!idToken) return;

    // Use dedicated resetting flag so other loading states (like send message) don't
    // inadvertently disable the Reset button.
    setIsResetting(true);
    setError(null);

    try {
      const resetHeaders: Record<string, string> = {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      };
      if (selectedClientId) resetHeaders['X-Client-ID'] = selectedClientId;
      const response = await fetch(`${API_BASE_URL.replace(/\/$/, '')}/client/test_chat/reset_session`, {
        method: 'POST',
        headers: resetHeaders,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to reset test session');
      }

      // Clear messages and reset state
      setMessages([]);
      setIsTesting(false);
      await checkSessionStatus();
      await loadMessages(); // Load any initial system message

    } catch (err: any) {
      setError(err.message || 'Failed to reset session');
      console.error('Reset session error:', err);
    } finally {
      setIsResetting(false);
    }
  }, [idToken]);

  // If no idToken, provide disabled context
  if (!idToken) {
    const defaultContext: TestChatContextType = {
      messages: [],
      isTesting: false,
      isLoading: false,
      isResetting: false,
      error: null,
      sendTestMessage: async () => {},
      resetSession: async () => {},
      startSession: async () => {},
      sessionExists: false,
      messageCount: 0,
    };

    return (
      <TestChatContext.Provider value={defaultContext}>
        {children}
      </TestChatContext.Provider>
    );
  }

  return (
    <TestChatContext.Provider
      value={{
        messages,
        isTesting,
        isLoading,
        isResetting,
        error,
        sendTestMessage,
        resetSession,
        startSession,
        sessionExists,
        messageCount,
      }}
    >
      {children}
    </TestChatContext.Provider>
  );
};

export const useTestChat = (): TestChatContextType => {
  const context = useContext(TestChatContext);
  if (context === undefined) {
    throw new Error('useTestChat must be used within a TestChatProvider');
  }
  return context;
};